import { Box, ColorSwatch, Flex, Stack, useMantineTheme } from '@mantine/core';
import classNames from 'classnames';
import React, { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { CardV2 as Card, EmptyState, RadialBurstDiagram, Text, Well } from '@feathr/components';

import * as styles from './TargetAudienceOverview.css';

interface IProps {
  isSticky?: boolean;
  layout?: 'vertical' | 'horizontal';
  className?: string;
}

function TargetAudienceOverview({
  isSticky = false,
  layout = 'vertical',
  className,
}: IProps): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const isEmpty = false;

  return (
    <Card contentClassName={className} isSticky={isSticky} width={'full'}>
      <Card.Header title={t('Target Audience Overview')} />

      <Card.Content addVerticalGap={true} pixelatedBackground={isEmpty ? undefined : 'white'}>
        {isEmpty ? (
          <EmptyState label={t('No audience data available')} theme={'slate'} />
        ) : (
          <Well className={classNames(styles.well, styles[layout])} layout={layout} theme={'white'}>
            <Flex className={classNames(styles.chartWrapper, styles[layout])}>
              <RadialBurstDiagram countBlue={45000} countTeal={15000} />
            </Flex>

            <div className={classNames(styles.content, styles[layout])}>
              <Flex justify={'space-between'} px={'4'} py={'3'}>
                <Flex align={'flex-start'}>
                  <ColorSwatch color={'transparent'} mr={'2'} mt={'1'} size={'12'} />
                  <Box>
                    <Text.DefaultMedium>{t('Cookies')}</Text.DefaultMedium>
                    <Text.SmallRegular>{t('Your data')}</Text.SmallRegular>
                  </Box>
                </Flex>
                <Box>
                  <Text.DefaultSemibold>25,000</Text.DefaultSemibold>
                </Box>
              </Flex>

              <Stack className={styles.border} px={'4'} py={'3'}>
                <Text.DefaultSemibold>{t('Audience Discovery')}</Text.DefaultSemibold>

                <Flex justify={'space-between'}>
                  <Flex align={'flex-start'}>
                    <ColorSwatch color={theme.colors.teal[5]} mr={'2'} mt={'1'} size={'12'} />
                    <Box>
                      <Text.DefaultMedium>{t('Email Activation')}</Text.DefaultMedium>
                      <Text.SmallRegular>{t('Email discovery network')}</Text.SmallRegular>
                    </Box>
                  </Flex>
                  <Box>
                    <Text.DefaultSemibold>15,000</Text.DefaultSemibold>
                  </Box>
                </Flex>

                <Flex justify={'space-between'}>
                  <Flex align={'flex-start'}>
                    <ColorSwatch color={theme.colors.blue[6]} mr={'2'} mt={'1'} size={'12'} />
                    <Box>
                      <Text.DefaultMedium>{t('Device Discovery')}</Text.DefaultMedium>
                      <Text.SmallRegular>{t('Via omni targeting')}</Text.SmallRegular>
                    </Box>
                  </Flex>
                  <Box ta={'right'}>
                    <Text.DefaultSemibold>~45,000</Text.DefaultSemibold>
                    <Text.SmallRegular c={'blue.6'}>
                      {t('+{{percentage}}% Expansion', { percentage: 40 })}
                    </Text.SmallRegular>
                  </Box>
                </Flex>
              </Stack>
            </div>
          </Well>
        )}
      </Card.Content>
    </Card>
  );
}

export default TargetAudienceOverview;
